import { Layers, Beaker } from 'lucide-react';
import type { BlendedParameters } from '../types/calculator';

interface BlendedSummaryProps {
  blended: BlendedParameters;
}

const safeNum = (n: number | undefined | null) => (typeof n === 'number' ? n : 0);

export default function BlendedSummary({ blended }: BlendedSummaryProps) {
  const p = blended?.params;

  if (!p) {
    return (
      <div className="rb-card p-8 text-center">
        <Beaker className="w-8 h-8 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-400">No blended parameters available.</p>
      </div>
    );
  }

  const flowRate = safeNum(p.flowRate);
  const bod5 = safeNum(p.bod5);
  const cod = safeNum(p.cod);
  const tss = safeNum(p.tss);
  const tds = safeNum(p.tds);
  const conductivity = safeNum(p.conductivity);
  const ph = safeNum(p.ph);
  const totalN = safeNum(p.totalNitrogen);
  const tkn = safeNum(p.tkn);
  const totalP = safeNum(p.totalPhosphorus);
  const oilG = safeNum(p.oilGrease);
  const temp = safeNum(p.temperature);
  const ratio = cod > 0 ? bod5 / cod : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
          <Beaker className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white">Blended Wastewater Assessment</h2>
          <p className="text-xs text-slate-500">Combined stream parameters after dilution and mixing calculation</p>
        </div>
      </div>

      {/* Stream Contributions */}
      <div className="flex flex-wrap justify-center gap-3">
        {(blended?.contributions ?? []).map((c, i) => (
          <div key={i} className={`px-4 py-3 rounded-xl border text-center min-w-[140px] ${
            i === 0 ? 'bg-cyan-500/5 border-cyan-500/20' : i === 1 ? 'bg-rb-brand/5 border-rb-brand/20' : 'bg-slate-800/30 border-slate-700'
          }`}>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{c.name}</div>
            <div className="text-lg font-bold text-white mt-1">{safeNum(c.flowRate as number)} <span className="text-xs font-normal text-slate-500">m³/day</span></div>
            <div className="text-xs text-slate-400">{safeNum(c.percentage as number)}% of total</div>
          </div>
        ))}
        <div className="px-4 py-3 rounded-xl border border-slate-700 bg-slate-800/30 text-center min-w-[140px]">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Total Blended</div>
          <div className="text-lg font-bold text-white mt-1">{flowRate.toFixed(1)} <span className="text-xs font-normal text-slate-500">m³/day</span></div>
        </div>
      </div>

      {/* Blended Parameters Grid */}
      <div className="rounded-xl border border-slate-800/50 p-5 bg-slate-900/20">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {[
            { label: 'BOD₅', value: bod5.toFixed(0), unit: 'mg/L', color: 'text-cyan-400' },
            { label: 'COD', value: cod.toFixed(0), unit: 'mg/L', color: 'text-rb-brand' },
            { label: 'TSS', value: tss.toFixed(0), unit: 'mg/L', color: 'text-slate-300' },
            { label: 'TDS', value: tds.toFixed(0), unit: 'mg/L', color: 'text-cyan-400' },
            { label: 'Conductivity', value: conductivity.toFixed(0), unit: 'µS/cm', color: 'text-rb-brand' },
            { label: 'pH', value: ph.toFixed(2), unit: '-', color: 'text-cyan-400' },
            { label: 'Total N', value: totalN.toFixed(0), unit: 'mg/L', color: 'text-slate-300' },
            { label: 'TKN', value: tkn.toFixed(0), unit: 'mg/L', color: 'text-slate-300' },
            { label: 'Total P', value: totalP.toFixed(0), unit: 'mg/L', color: 'text-slate-300' },
            { label: 'O&G', value: oilG.toFixed(0), unit: 'mg/L', color: 'text-rb-brand' },
            { label: 'Temperature', value: temp.toFixed(1), unit: '°C', color: 'text-cyan-400' },
            { label: 'BOD/COD', value: ratio.toFixed(2), unit: 'ratio', color: 'text-slate-300' },
          ].map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-800/50 text-center hover:border-slate-600 transition-colors">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{item.label}</div>
              <div className={`text-xl font-bold ${item.color} mt-1`}>{item.value}</div>
              <div className="text-xs text-slate-600">{item.unit}</div>
            </div>
          ))}
        </div>

        {/* Dilution Note */}
        <div className="mt-4 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
          <p className="text-xs text-slate-400 leading-relaxed">
            <span className="text-cyan-400 font-bold">Blending Method:</span> Weighted average based on flow contributions. Each parameter is calculated as <em className="text-slate-300">Σ(flowᵢ × paramᵢ) / Σ(flowᵢ)</em>. pH is calculated as weighted average of hydrogen ion concentrations.
          </p>
        </div>
      </div>
    </div>
  );
}
